import { Col, Row } from "antd";
import { ChatSummaryStats } from "types/";
import { useAnalyticsPage } from "./hooks/useAnalyticsPage";
import { AnalyticsSubPageLayout } from "../components/analytics/AnalyticsSubPageLayout";
import { CompactStatCard } from "../components/analytics/CompactStatCard";
import { PersuasionOutcomeChart } from "../components/analytics/PersuasionOutcomeChart";
import { QuestionTypesChart } from "../components/analytics/QuestionTypesChart";
import { SentimentDistributionChart } from "../components/analytics/SentimentDistributionChart";
import { TARGETS } from "../components/analytics/constants/constants";
import { ViewChatHistoryLink } from "./components/ViewChatHistoryLink";

const T_PREFIX = "media.projects.analytics.aiChatbot";

export function AIChatbotAnalyticsPage() {
  const page = useAnalyticsPage<ChatSummaryStats>({ analyticsType: "chat" });
  const { summaryData: chatData, isDataLoading, t } = page;

  return (
    <AnalyticsSubPageLayout
      authKey="aiChatbotAnalytics"
      translationPrefix={T_PREFIX}
      page={page}
      titleExtra={
        page.projectId ? (
          <ViewChatHistoryLink projectId={page.projectId} />
        ) : undefined
      }
    >
      <Row gutter={[8, 8]} style={{ width: "100%" }}>
        <Col sm={24} md={6}>
          <CompactStatCard
            label={t(`${T_PREFIX}.chatSessions`)}
            metric={chatData?.total_chat_sessions}
            loading={isDataLoading}
          />
        </Col>
        <Col sm={24} md={6}>
          <CompactStatCard
            label={t(`${T_PREFIX}.chatRate`)}
            metric={chatData?.chat_rate_pct}
            target={TARGETS.aiChatbot.chat_rate_pct}
            aim="high"
            type="percentage"
            loading={isDataLoading}
          />
        </Col>
        <Col sm={24} md={6}>
          <CompactStatCard
            label={t(`${T_PREFIX}.avgMessages`)}
            metric={chatData?.avg_messages_sent}
            loading={isDataLoading}
          />
        </Col>
        <Col sm={24} md={6}>
          <CompactStatCard
            label={t(`${T_PREFIX}.avgChatDuration`)}
            metric={chatData?.avg_chat_duration_seconds}
            target={TARGETS.aiChatbot.avg_chat_duration_seconds}
            aim="high"
            type="seconds"
            loading={isDataLoading}
          />
        </Col>
      </Row>

      <Row gutter={[8, 8]} style={{ width: "100%" }}>
        <Col sm={24} md={6}>
          <CompactStatCard
            label={t(`${T_PREFIX}.sourceClicks`)}
            metric={chatData?.total_source_clicks}
            loading={isDataLoading}
          />
        </Col>
        <Col sm={24} md={6}>
          <CompactStatCard
            label={t(`${T_PREFIX}.suggestionsUsed`)}
            metric={chatData?.total_suggestion_selects}
            loading={isDataLoading}
          />
        </Col>
        <Col sm={24} md={6}>
          <CompactStatCard
            label={t(`${T_PREFIX}.historyExports`)}
            metric={chatData?.total_history_exports}
            loading={isDataLoading}
          />
        </Col>
        <Col sm={24} md={6}>
          <CompactStatCard
            label={t(`${T_PREFIX}.confusionScore`)}
            metric={chatData?.avg_confusion_score}
            target={TARGETS.aiChatbot.avg_confusion_score}
            aim="low"
            loading={isDataLoading}
            tooltip={t(`${T_PREFIX}.confusionScoreTooltip`)}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ width: "100%" }}>
        <Col sm={24} md={8}>
          <PersuasionOutcomeChart data={chatData} loading={isDataLoading} />
        </Col>
        <Col sm={24} md={8}>
          <QuestionTypesChart data={chatData} loading={isDataLoading} />
        </Col>
        <Col sm={24} md={8}>
          <SentimentDistributionChart data={chatData} loading={isDataLoading} />
        </Col>
      </Row>
    </AnalyticsSubPageLayout>
  );
}
